<script lang="ts">
	import uqnSplit from './functions/uqnSplit';
	import Fraction from './functions/fractionClass';
	import katexify from './functions/katexify';

	export let uqn: number;
	let ansA: string, ansB: string, ansC: string, ansD: string;
	$: [ansA, ansB, ansC, ansD] = uqnToAns2203(uqn);

	function uqnToAns2203(u:number):string[]{
		const [type,a,b,c,k] = uqnSplit(u);
		let ansA:string, ansB:string, ansC:string;
		const aFrac = new Fraction(a,10);
		const bFrac = new Fraction(b,10);
		const cFrac = new Fraction(c,10);
		const oneFrac = new Fraction(1,1);
		if (type<6){ // types 1 and 2
			if (type<5){ // 1: a cap b, 2: a | b, 3: a | b', 4: a' | b
				// intersect must be between a+b-1 and min(a,b);
				const upperBound = Math.min(a,b);
				const lowerBound = Math.max(a+b-10,0);
				const intersectFrac = new Fraction(10*lowerBound+k*(upperBound-lowerBound),100);
				const aUbFrac = aFrac.add(bFrac).minus(intersectFrac);
				const aPrimeIntersectCFrac = oneFrac.minus(aFrac).multiply(cFrac);
				ansA = `P(A \\cup B) = ${aUbFrac.toString()}`;
				ansB = `P(A' \\cap C) = ${aPrimeIntersectCFrac.toString()}`;
				// determine min value
				const min1 = bFrac.minus(intersectFrac).minus(oneFrac);
				let minFrac: Fraction;
				minFrac = (min1.eval()>0)? min1 : new Fraction(0,1);
				const min2 = cFrac.minus(aFrac.multiply(cFrac)).minus(oneFrac);
				minFrac = (min2.eval()>minFrac.eval())? min2 : minFrac;
				const min3 = aFrac.add(bFrac).add(cFrac).minus(oneFrac).minus(intersectFrac).minus(aFrac.multiply(cFrac));
				minFrac = (min3.eval()>minFrac.eval())? min3 : minFrac;
				// determine max value
				const max1 = bFrac.minus(intersectFrac);
				let maxFrac = max1;
				const max2 = cFrac.minus(aFrac.multiply(cFrac));
				maxFrac = (max2.eval()<maxFrac.eval())? max2 : maxFrac;
				const max3 = aFrac.add(bFrac).add(cFrac).minus(intersectFrac).minus(aFrac.multiply(cFrac));
				maxFrac = (max3.eval()<maxFrac.eval())? max3 : maxFrac;
				ansC = `${minFrac.toString()} \\leq P(A'\\cap B \\cap C) \\leq ${maxFrac.toString()}`;
			} else{ // 5: given a intersect b intersect c.
				const upperBound = Math.min(a*b,a*c,b*c,a*b+a*c-a*10+100,a*b+b*c-10*b+100,a*c+b*c-10*c+100,100);
				const lowerBound = Math.max(a*b-100,a*c-100,b*c-100,a*b+a*c-a*10,a*b+b*c-10*b,a*c+b*c-10*c,0);
				const intersectFrac = new Fraction(10*lowerBound+k*(upperBound-lowerBound),1000);
				const aUbFrac = aFrac.add(bFrac).minus(aFrac.multiply(bFrac));
				ansA = `P(A \\cup B) = ${aUbFrac.toString()}`;
				const outsideFrac = oneFrac.minus(aFrac).minus(bFrac).minus(cFrac).add(aFrac.multiply(bFrac)).add(aFrac.multiply(cFrac)).add(bFrac.multiply(cFrac)).minus(intersectFrac);
				ansB = `P(A' \\cap B' \\cap C') = ${outsideFrac.toString()}`;
				// determine min value
				const min1 = bFrac.minus(aFrac.multiply(bFrac)).minus(oneFrac);
				let minFrac: Fraction;
				minFrac = (min1.eval()>0)? min1 : new Fraction(0,1);
				const min2 = cFrac.minus(aFrac.multiply(cFrac)).minus(oneFrac);
				minFrac = (min2.eval()>minFrac.eval())? min2 : minFrac;
				const min3 = aFrac.add(bFrac).add(cFrac).minus(oneFrac).minus(aFrac.multiply(bFrac)).minus(aFrac.multiply(cFrac));
				minFrac = (min3.eval()>minFrac.eval())? min3 : minFrac;
				// determine max value
				const max1 = bFrac.minus(aFrac.multiply(bFrac));
				let maxFrac = max1;
				const max2 = cFrac.minus(aFrac.multiply(cFrac));
				maxFrac = (max2.eval()<maxFrac.eval())? max2 : maxFrac;
				const max3 = aFrac.add(bFrac).add(cFrac).minus(aFrac.multiply(bFrac)).minus(aFrac.multiply(cFrac));
				maxFrac = (max3.eval()<maxFrac.eval())? max3 : maxFrac;
				// finding outisde
				const finalMinFrac = oneFrac.minus(aFrac).minus(bFrac).minus(cFrac).add(aFrac.multiply(bFrac)).add(aFrac.multiply(cFrac)).add(minFrac);
				const finalMaxFrac = oneFrac.minus(aFrac).minus(bFrac).minus(cFrac).add(aFrac.multiply(bFrac)).add(aFrac.multiply(cFrac)).add(maxFrac);
				ansC = `P(A'\\cap B' \\cap C') = ${finalMinFrac.toString()}`;
				ansD = `P(A'\\cap B' \\cap C') = ${finalMaxFrac.toString()}`;
			}
			return [ansA, ansB, ansC, ansD]
		} else{ // 6: mutually exclusive
			if (k==1){ // a' cap b
				ansA = `P(A' \\cap B) = b - ab`;
			} else if (k==2){ // a cap b'
				ansA = `P(A \\cap B') = a - ab`;
			} else{
				ansA = `P(A' \\cap B') = 1 - a - b + ab`;
			}
			const kFrac = new Fraction(b*(10-a),100);
			const mFrac = new Fraction(c*(10-a),100);
			const minFrac = kFrac.divide(oneFrac.minus(aFrac)).multiply(aFrac);
			const maxFrac = (oneFrac.minus(aFrac).minus(mFrac)).divide(oneFrac.minus(aFrac)).multiply(aFrac);
			ansB = `P(A \\cap B) = ${minFrac.toString()}`;
			ansC = `P(A \\cap B) = ${maxFrac.toString()}`;
			return [ansA, ansB, ansC]
		}
	}
</script>

<p>
	(a) {@html katexify(ansA+".")}
</p>
{#if Number(uqn.toString()[0])<5}
	<p>
		(b) {@html katexify(ansB+".")}
	</p>
	<p>
		(c) {@html katexify(ansC+".")}
	</p>
{:else if Number(uqn.toString()[0])==5}
	<p>
		(b) {@html katexify(ansB+".")}
	</p>
	<p>
		(c) Minimum {@html katexify(ansC+".")} <br>
		Maximum {@html katexify(ansD+".")}
	</p>
{:else}
	<p>
		(b) Least {@html katexify(ansB+".")} <br>
		Greatest {@html katexify(ansC+".")}
	</p>
{/if}